"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import { API_BASE } from "@/lib/auth";
import { TourButton } from "@/components/tours/TourButton";
import { useAutoTour } from "@/components/tours/useAutoTour";
import { TOURS } from "@/components/tours/catalog";
import { StaffEmailInsights, type EmailSummary, type EmailLogRow, type DraftEditRow } from "./StaffEmailInsights";

// Brand palette
const TEAL = "#007a8a";
const DARK = "#003d45";
const CHART_PALETTE = ["#003d45", "#007a8a", "#0891b2", "#38bdf8", "#94a3b8", "#f59e0b", "#dc2626"];
const TOOLTIP = {
  backgroundColor: "#0f172a",
  borderColor: "#0f172a",
  textStyle: { color: "#f1f5f9", fontSize: 11 },
} as const;

type Range = "1d" | "7d" | "30d" | "90d";

interface UserRef { id: string; name: string | null; email: string | null; role: string | null; }

interface Summary {
  range: string;
  totals: {
    active_users: number; api_calls: number; page_views: number; clicks: number;
    group_edits: number; entities_created: number;
    errors_4xx: number; errors_5xx: number;
    llm_calls: number; llm_failed: number;
    llm_total_tokens: number; llm_cost_usd: number;
    serp_calls: number; serp_failed: number;
    apify_calls: number; apify_failed: number; apify_cost_usd: number;
    uploads: number;
    logins: number; login_failures: number;
  };
  top_pages: { path: string; count: number }[];
  top_clicks: { track_key: string; count: number }[];
  top_routes: { path: string; count: number }[];
  top_editors: { user: UserRef | null; count: number }[];
  top_active_users: { user: UserRef | null; count: number }[];
}

interface ApiCallRow {
  id: string; user: UserRef | null; method: string; path: string;
  query_string: string | null; status_code: number | null; duration_ms: number | null;
  ip: string | null; user_agent: string | null; referer: string | null;
  error_detail: string | null; request_size: number | null; response_size: number | null;
  request_id: string | null; created_at: string;
}

interface LlmRow {
  id: string; user: UserRef | null; request_id: string | null;
  provider: string; model: string | null; purpose: string | null;
  prompt_summary: string | null; success: boolean; status_code: number | null;
  error_type: string | null; error_detail: string | null;
  duration_ms: number | null; input_tokens: number | null; output_tokens: number | null;
  extra: Record<string, unknown> | null; created_at: string;
}

interface LlmSummaryRow {
  provider: string; purpose: string | null; count: number; failures: number;
  avg_ms: number; input_tokens: number; output_tokens: number; cost_usd: number;
}

interface SerpRow {
  id: string; user: UserRef | null; purpose: string | null;
  query_summary: string | null; success: boolean; status_code: number | null;
  error_type: string | null; error_detail: string | null;
  duration_ms: number | null; result_count: number | null;
  extra: Record<string, unknown> | null; created_at: string;
}

interface SerpSummaryRow {
  purpose: string | null; count: number; failures: number;
  avg_ms: number; total_results: number; cost_usd: number;
}

interface UploadSummaryRow {
  type: string; events: number; files: number; uploaders: number;
}

interface AuthSummaryRow { event: string; count: number; users: number; }
interface CocRow { id: string; user: UserRef | null; version: string; ip: string | null; accepted_at: string | null; }
interface CocSummary { total: number; unique_users: number; rows: CocRow[]; }
interface AuthEventRow {
  id: string; user: UserRef | null; event: string;
  ip: string | null; user_agent: string | null; created_at: string | null;
}

interface ActiveUserRow {
  user: UserRef | null;
  api_calls: number; page_views: number; clicks: number; edits: number;
  last_seen: string | null;
}

interface GroupActivityRow {
  group_type: string; group_id: string;
  edits: number; editors: number; last_edited: string | null;
}

interface AuditLogRow {
  id: string; created_at: string; user: UserRef | null;
  kind: "field_edit" | "action"; entity_type: string; entity_id: string;
  action: string; field_path: string | null;
  old_value: unknown; new_value: unknown;
  summary: Record<string, unknown> | null; label: string;
}

interface DauPoint { day: string; dau: number; calls: number; }
interface EventsPoint { day: string; page_views: number; clicks: number; api_calls: number; }
interface DeviceBreakdown {
  browsers: { label: string; count: number }[];
  os: { label: string; count: number }[];
  devices: { label: string; count: number }[];
}
interface SessionSummary {
  sessions: number; avg_pages_per_session: number;
  avg_duration_secs: number; bounce_rate_pct: number;
}
interface SessionRow {
  session_id: string; user: UserRef | null; pages: number; duration_secs: number;
  entry_page: string | null; exit_page: string | null; started: string | null;
}
interface InteractionRow {
  id: string; user: UserRef | null; kind: string; path: string | null;
  session_id: string | null; payload: Record<string, unknown> | null; created_at: string;
}
interface HeatCell { weekday: number; hour: number; count: number; }
interface Heatmap { tz: string; cells: HeatCell[]; peak: { weekday: number; hour: number; count: number }; }
interface StatusRow { cls: string; count: number; }
interface RoleRow { role: string; users: number; }
interface SessionEvent { ts: string; kind: string; path?: string | null;
  duration_ms?: number | null; max_scroll_pct?: number | null;
  track_key?: string | null; label?: string | null; }
interface SlowRoute { path: string; method: string; count: number;
  avg_ms: number; p95_ms: number; max_ms: number; }


async function api<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}/api/v1/insights${path}`, {
    credentials: "include",  // auth cookie sent automatically
    ...options,
  });
  if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
  return res.json() as Promise<T>;
}

function userLabel(user: UserRef | null): string {
  if (!user) return "Anonymous";
  return user.name || user.email || user.id.slice(0, 8);
}

function relTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso); const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function fmtMs(ms: number | null | undefined): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function fmtDuration(secs: number | null | undefined): string {
  if (secs == null || secs <= 0) return "—";
  if (secs < 60) return `${Math.round(secs)}s`;
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

// Instant hover tooltip (native `title=` has a ~1s browser delay, which felt broken).
function InfoTooltip({ text }: { text: string }) {
  return (
    <span className="relative inline-flex group">
      <span className="cursor-help text-slate-400 group-hover:text-slate-600 text-[10px] leading-none border border-slate-300 rounded-full w-3.5 h-3.5 inline-flex items-center justify-center font-bold">i</span>
      <span className="pointer-events-none absolute left-1/2 top-full z-50 mt-1.5 hidden w-72 -translate-x-1/2 group-hover:block rounded-lg bg-slate-900 px-3 py-2.5 text-[11px] font-normal normal-case tracking-normal leading-relaxed text-slate-100 shadow-xl whitespace-pre-line">
        {text}
      </span>
    </span>
  );
}

function Card({ label, value, tone, hint, tooltip }: { label: string; value: number | string; tone?: "danger" | "warn" | "ok"; hint?: string; tooltip?: string }) {
  const color = tone === "danger" ? "text-red-600" : tone === "warn" ? "text-amber-600" : "text-slate-900";
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1">
        {label}
        {tooltip && <InfoTooltip text={tooltip} />}
      </p>
      <p className={`text-3xl font-bold mt-2 ${color}`}>{value}</p>
      {hint && <p className="text-[10px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function RankedList({ title, rows }: { title: string; rows: { label: string; count: number }[] }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <p className="text-xs font-black uppercase tracking-widest text-slate-700 mb-3">{title}</p>
      <div className="space-y-2">
        {rows.length === 0 && <p className="text-xs text-slate-400">No data yet.</p>}
        {rows.map((r, i) => (
          <div key={`${r.label}-${i}`} className="text-xs">
            <div className="flex justify-between items-center mb-1">
              <span className="font-mono text-slate-700 truncate pr-2">{r.label || "(unknown)"}</span>
              <span className="font-bold text-slate-900">{r.count}</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-[#003d45]" style={{ width: `${(r.count / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChartCard({ title, hint, children, className }: {
  title: string; hint?: string; children: React.ReactNode; className?: string;
}) {
  return (
    <div className={`bg-white rounded-xl border border-slate-200 p-5 ${className || ""}`}>
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <p className="text-xs font-black uppercase tracking-widest text-slate-700">{title}</p>
        {hint && <p className="text-[10px] text-slate-400">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

const AXIS = {
  axisLine: { lineStyle: { color: "#e2e8f0" } },
  axisLabel: { color: "#94a3b8", fontSize: 10 },
  splitLine: { lineStyle: { color: "#f1f5f9" } },
};

// Shared interactivity: drag-to-zoom + a scrub slider. Reused by the time-series charts.
const DATA_ZOOM = [
  { type: "inside" as const },
  { type: "slider" as const, height: 14, bottom: 6, borderColor: "transparent",
    fillerColor: "rgba(0,122,138,0.12)", handleStyle: { color: TEAL },
    textStyle: { color: "#94a3b8", fontSize: 9 } },
];

function DauChart({ data, onDrillDay }: { data: DauPoint[]; onDrillDay?: (day: string) => void }) {
  const peak = Math.max(1, ...data.map((d) => d.dau));
  const option = {
    grid: { top: 28, right: 16, bottom: 44, left: 32 },
    tooltip: { trigger: "axis", ...TOOLTIP,
      formatter: (p: { name: string; data: number }[]) =>
        `${p[0].name}<br/>${p[0].data} active users` },
    dataZoom: DATA_ZOOM,
    xAxis: { type: "category", data: data.map((d) => d.day.slice(5)), boundaryGap: false, ...AXIS },
    yAxis: { type: "value", minInterval: 1, ...AXIS },
    series: [{
      type: "line", smooth: true, symbol: "circle", symbolSize: 5,
      data: data.map((d) => d.dau),
      lineStyle: { color: DARK, width: 2 }, itemStyle: { color: TEAL },
      areaStyle: { color: "rgba(0,122,138,0.14)" },
    }],
  };
  const onEvents = onDrillDay
    ? { click: (p: { dataIndex: number }) => { const d = data[p.dataIndex]; if (d) onDrillDay(d.day.slice(0, 10)); } }
    : undefined;
  return (
    <ChartCard title="Daily active users (30d)" hint={onDrillDay ? `peak ${peak} · click a day` : `peak ${peak}`}>
      {data.length === 0
        ? <p className="text-xs text-slate-400">No data.</p>
        : <ReactECharts option={option} style={{ height: 200 }} notMerge onEvents={onEvents} />}
    </ChartCard>
  );
}

function EventsTrendChart({ data, onDrillDay }: { data: EventsPoint[]; onDrillDay?: (day: string) => void }) {
  const series = [
    { name: "Page views", key: "page_views", color: DARK },
    { name: "Clicks", key: "clicks", color: TEAL },
    { name: "API calls", key: "api_calls", color: "#94a3b8" },
  ];
  const option = {
    color: series.map((s) => s.color),
    grid: { top: 30, right: 16, bottom: 48, left: 36 },
    tooltip: { trigger: "axis", ...TOOLTIP },
    legend: { top: 0, left: 0, textStyle: { color: "#64748b", fontSize: 10 }, itemHeight: 8, itemWidth: 12 },
    dataZoom: DATA_ZOOM,
    xAxis: { type: "category", data: data.map((d) => d.day.slice(5)), boundaryGap: false, ...AXIS },
    yAxis: { type: "value", ...AXIS },
    series: series.map((s) => ({
      name: s.name, type: "line", smooth: true, showSymbol: false,
      data: data.map((d) => d[s.key as keyof EventsPoint] as number),
    })),
  };
  const onEvents = onDrillDay
    ? { click: (p: { dataIndex: number }) => { const d = data[p.dataIndex]; if (d) onDrillDay(d.day.slice(0, 10)); } }
    : undefined;
  return (
    <ChartCard title="Activity over time (30d)" hint={onDrillDay ? "page views · clicks · API calls — click a day" : "page views · clicks · API calls"}>
      {data.length === 0
        ? <p className="text-xs text-slate-400">No data.</p>
        : <ReactECharts option={option} style={{ height: 200 }} notMerge onEvents={onEvents} />}
    </ChartCard>
  );
}

function BarChart({ rows, color, onDrillLabel }: { rows: { label: string; count: number }[]; color?: string; onDrillLabel?: (label: string) => void }) {
  if (!rows.length) return <p className="text-xs text-slate-400">No data yet.</p>;
  const ordered = [...rows].reverse(); // ECharts horizontal bars draw bottom-up
  const option = {
    grid: { top: 8, right: 24, bottom: 8, left: 8, containLabel: true },
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, ...TOOLTIP },
    xAxis: { type: "value", ...AXIS, splitLine: { lineStyle: { color: "#f1f5f9" } } },
    yAxis: {
      type: "category", data: ordered.map((r) => r.label || "(unknown)"),
      axisLine: { show: false }, axisTick: { show: false },
      axisLabel: { color: "#475569", fontSize: 10, width: 180, overflow: "truncate" },
    },
    series: [{
      type: "bar", data: ordered.map((r) => r.count), barMaxWidth: 16,
      cursor: onDrillLabel ? "pointer" : "default",
      itemStyle: { color: color || DARK, borderRadius: [0, 3, 3, 0] },
      label: { show: true, position: "right", color: "#94a3b8", fontSize: 10 },
    }],
  };
  const onEvents = onDrillLabel
    ? { click: (p: { dataIndex: number }) => { const r = ordered[p.dataIndex]; if (r) onDrillLabel(r.label); } }
    : undefined;
  return <ReactECharts option={option} style={{ height: Math.max(120, rows.length * 26 + 20) }} notMerge onEvents={onEvents} />;
}

function Donut({ rows }: { rows: { label: string; count: number }[] }) {
  if (!rows.length) return <p className="text-xs text-slate-400">No data yet.</p>;
  const option = {
    color: CHART_PALETTE,
    tooltip: { trigger: "item", ...TOOLTIP, formatter: "{b}: {c} ({d}%)" },
    legend: { type: "scroll", bottom: 0, textStyle: { color: "#64748b", fontSize: 10 }, itemHeight: 8, itemWidth: 12 },
    series: [{
      type: "pie", radius: ["48%", "72%"], center: ["50%", "44%"], avoidLabelOverlap: true,
      itemStyle: { borderColor: "#fff", borderWidth: 2 },
      label: { show: false }, labelLine: { show: false },
      data: rows.map((r) => ({ name: r.label || "unknown", value: r.count })),
    }],
  };
  return <ReactECharts option={option} style={{ height: 200 }} notMerge />;
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function range24(): number[] { return Array.from({ length: 24 }, (_, i) => i); }

function fmtHour12(h: number): string {
  const period = h < 12 ? "am" : "pm";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}${period}`;
}

function ActivityHeatmap({ data }: { data: Heatmap | null }) {
  const tzLabel = data ? (data.tz.split("/").pop()?.replace("_", " ") || data.tz) : "";
  const peak = data?.peak;
  const max = Math.max(1, ...(data?.cells.map((c) => c.count) || [1]));
  // ECharts heatmap rows draw bottom-up, so reverse weekday order (Mon at top).
  const yLabels = [...WEEKDAYS].reverse();
  const points = (data?.cells || []).map((c) => [c.hour, 6 - c.weekday, c.count]);
  const hourLabels = range24().map((h) => (h % 3 === 0 ? fmtHour12(h) : ""));

  const option = {
    grid: { top: 8, right: 12, bottom: 56, left: 36 },
    tooltip: {
      ...TOOLTIP, position: "top",
      formatter: (p: { data: number[] }) =>
        `${yLabels[p.data[1]]} ${fmtHour12(p.data[0])}<br/>${p.data[2]} requests`,
    },
    xAxis: {
      type: "category", data: hourLabels, splitArea: { show: false },
      axisLine: { show: false }, axisTick: { show: false },
      axisLabel: { color: "#94a3b8", fontSize: 9, interval: 0 },
    },
    yAxis: {
      type: "category", data: yLabels, splitArea: { show: false },
      axisLine: { show: false }, axisTick: { show: false },
      axisLabel: { color: "#475569", fontSize: 10 },
    },
    visualMap: {
      min: 0, max, calculable: true, orient: "horizontal", left: "center", bottom: 6,
      itemWidth: 12, itemHeight: 120, text: ["more", "less"],
      textStyle: { color: "#94a3b8", fontSize: 9 },
      inRange: { color: ["#f1f5f9", "#9fd5dc", TEAL, DARK] },
    },
    series: [{
      type: "heatmap", data: points,
      itemStyle: { borderColor: "#fff", borderWidth: 2, borderRadius: 3 },
      emphasis: { itemStyle: { shadowBlur: 6, shadowColor: "rgba(0,0,0,0.2)" } },
      progressive: 0,
    }],
  };

  return (
    <ChartCard
      title="Activity heatmap — by day & hour"
      hint={data && peak && peak.count > 0
        ? `times in ${tzLabel} · busiest ${WEEKDAYS[peak.weekday]} ${fmtHour12(peak.hour)} (${peak.count})`
        : tzLabel ? `times in ${tzLabel}` : undefined}
    >
      {!data || !data.cells.length
        ? <p className="text-xs text-slate-400">No data yet.</p>
        : <ReactECharts option={option} style={{ height: 240 }} notMerge />}
    </ChartCard>
  );
}

function StatusBreakdown({ data, onDrillStatus }: { data: StatusRow[]; onDrillStatus?: (cls: string) => void }) {
  const total = data.reduce((s, r) => s + r.count, 0);
  const colors: Record<string, string> = { "2xx": "#16a34a", "3xx": "#0891b2", "4xx": "#d97706", "5xx": "#dc2626" };
  const labels: Record<string, string> = { "2xx": "Success (2xx)", "3xx": "Redirect (3xx)", "4xx": "Client error (4xx)", "5xx": "Server error (5xx)" };
  const labelToCls: Record<string, string> = Object.fromEntries(Object.entries(labels).map(([k, v]) => [v, k]));
  const option = {
    tooltip: { trigger: "item", ...TOOLTIP, formatter: "{b}: {c} ({d}%)" },
    legend: {
      type: "scroll", bottom: 0, icon: "circle",
      textStyle: { color: "#64748b", fontSize: 10 }, itemHeight: 8, itemWidth: 8,
    },
    series: [{
      type: "pie", radius: ["48%", "72%"], center: ["50%", "44%"], avoidLabelOverlap: false,
      cursor: onDrillStatus ? "pointer" : "default",
      itemStyle: { borderColor: "#fff", borderWidth: 2 },
      label: { show: false }, labelLine: { show: false },
      data: data.filter((r) => r.count > 0).map((r) => ({
        name: labels[r.cls], value: r.count, itemStyle: { color: colors[r.cls] },
      })),
    }],
  };
  const onEvents = onDrillStatus
    ? { click: (p: { name: string }) => { const cls = labelToCls[p.name]; if (cls) onDrillStatus(cls); } }
    : undefined;
  return (
    <ChartCard title="Request health by status" hint={total ? (onDrillStatus ? `${total} requests · click a slice` : `${total} requests`) : undefined}>
      {total === 0
        ? <p className="text-xs text-slate-400">No data yet.</p>
        : <ReactECharts option={option} style={{ height: 200 }} notMerge onEvents={onEvents} />}
    </ChartCard>
  );
}

function ApiCallTable({ rows, title }: { rows: ApiCallRow[]; title: string }) {
  const [expanded, setExpanded] = useState<string | null>(null);
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-200">
        <p className="text-xs font-black uppercase tracking-widest text-slate-700">{title}</p>
      </div>
      <div className="overflow-x-auto max-h-[560px]">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest sticky top-0">
            <tr>
              <th className="text-left px-4 py-2">Time</th>
              <th className="text-left px-4 py-2">Method</th>
              <th className="text-left px-4 py-2">Path</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Duration</th>
              <th className="text-left px-4 py-2">User</th>
              <th className="text-left px-4 py-2">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => {
              const isErr = (r.status_code || 0) >= 400;
              const open = expanded === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr className={`cursor-pointer hover:bg-slate-50 ${isErr ? "bg-red-50/40" : ""}`}
                      onClick={() => setExpanded(open ? null : r.id)}>
                    <td className="px-4 py-2 text-slate-500 font-mono">{new Date(r.created_at).toLocaleTimeString()}</td>
                    <td className="px-4 py-2 font-bold font-mono">{r.method}</td>
                    <td className="px-4 py-2 truncate max-w-md font-mono">{r.path}</td>
                    <td className={`px-4 py-2 font-bold font-mono ${
                      (r.status_code || 0) >= 500 ? "text-red-600" :
                      (r.status_code || 0) >= 400 ? "text-amber-600" : "text-slate-600"
                    }`}>{r.status_code ?? "—"}</td>
                    <td className="px-4 py-2 text-slate-500 font-mono">{fmtMs(r.duration_ms)}</td>
                    <td className="px-4 py-2">
                      <span className={r.user ? "text-slate-700" : "text-slate-400 italic"}>{userLabel(r.user)}</span>
                    </td>
                    <td className="px-4 py-2 text-slate-400 font-mono">{r.ip ?? "—"}</td>
                  </tr>
                  {open && (
                    <tr className="bg-slate-50">
                      <td colSpan={7} className="px-6 py-3 text-xs space-y-1 font-mono text-slate-600">
                        {r.user?.email && <div><b>email:</b> {r.user.email}</div>}
                        {r.query_string && <div><b>query:</b> ?{r.query_string}</div>}
                        {r.referer && <div><b>referer:</b> {r.referer}</div>}
                        {r.user_agent && <div className="truncate"><b>ua:</b> {r.user_agent}</div>}
                        {r.request_id && <div><b>request_id:</b> {r.request_id}</div>}
                        {(r.request_size || r.response_size) && (
                          <div><b>size:</b> req {r.request_size ?? "?"}B → resp {r.response_size ?? "?"}B</div>
                        )}
                        {r.error_detail && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-800 whitespace-pre-wrap break-all">
                            {r.error_detail}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface DrillFilter { title: string; query: string; }

// Slide-over drawer that loads the raw API-call rows behind whatever aggregate
// (a bar, a status slice, a day on the trend line) the user clicked.
function DrillDownDrawer({ filter, range, onClose }: { filter: DrillFilter; range: Range; onClose: () => void }) {
  const [rows, setRows] = useState<ApiCallRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setRows(null); setErr(null);
    api<ApiCallRow[]>(`/drilldown?range=${range}&${filter.query}&limit=200`)
      .then((d) => !cancelled && setRows(d))
      .catch((e) => !cancelled && setErr(String(e)));
    return () => { cancelled = true; };
  }, [filter.query, range]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} />
      <div className="relative bg-slate-50 w-full max-w-3xl h-full shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 bg-white flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Drill-down · last {range}</p>
            <p className="text-sm font-bold text-slate-800 mt-0.5">{filter.title}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-lg leading-none px-2">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {err && <p className="text-sm text-red-600">Error: {err}</p>}
          {!rows && !err && <p className="text-sm text-slate-500">Loading…</p>}
          {rows && rows.length === 0 && <p className="text-sm text-slate-400">No matching API calls in this range.</p>}
          {rows && rows.length > 0 && (
            <ApiCallTable rows={rows} title={`${rows.length} matching API call${rows.length === 1 ? "" : "s"}${rows.length >= 200 ? " (showing latest 200)" : ""}`} />
          )}
        </div>
      </div>
    </div>
  );
}

function LlmCallTable({ rows, title }: { rows: LlmRow[]; title: string }) {
  const [expanded, setExpanded] = useState<string | null>(null);
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-200">
        <p className="text-xs font-black uppercase tracking-widest text-slate-700">{title}</p>
      </div>
      <div className="overflow-x-auto max-h-[480px]">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest sticky top-0">
            <tr>
              <th className="text-left px-4 py-2">Time</th><th className="text-left px-4 py-2">Provider</th>
              <th className="text-left px-4 py-2">Purpose</th><th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Duration</th><th className="text-left px-4 py-2">Tokens</th>
              <th className="text-left px-4 py-2">User</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-400">No LLM calls yet.</td></tr>
            )}
            {rows.map((r) => {
              const open = expanded === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr className={`cursor-pointer hover:bg-slate-50 ${!r.success ? "bg-red-50/40" : ""}`}
                      onClick={() => setExpanded(open ? null : r.id)}>
                    <td className="px-4 py-2 text-slate-500 font-mono">{new Date(r.created_at).toLocaleTimeString()}</td>
                    <td className="px-4 py-2 font-mono">{r.provider}</td>
                    <td className="px-4 py-2 font-mono text-slate-600">{r.purpose ?? "—"}</td>
                    <td className={`px-4 py-2 font-bold font-mono ${r.success ? "text-slate-600" : "text-red-600"}`}>
                      {r.success ? "OK" : (r.error_type || "FAIL")}
                    </td>
                    <td className="px-4 py-2 text-slate-500 font-mono">{fmtMs(r.duration_ms)}</td>
                    <td className="px-4 py-2 text-slate-500 font-mono">
                      {r.input_tokens || r.output_tokens
                        ? `${r.input_tokens ?? 0}→${r.output_tokens ?? 0}` : "—"}
                    </td>
                    <td className="px-4 py-2">
                      <span className={r.user ? "text-slate-700" : "text-slate-400 italic"}>{userLabel(r.user)}</span>
                    </td>
                  </tr>
                  {open && (
                    <tr className="bg-slate-50">
                      <td colSpan={7} className="px-6 py-3 text-xs space-y-1 font-mono text-slate-600">
                        {r.model && <div><b>model:</b> {r.model}</div>}
                        {r.status_code && <div><b>status:</b> {r.status_code}</div>}
                        {r.extra && Object.keys(r.extra).length > 0 && (<div><b>extra:</b> {JSON.stringify(r.extra)}</div>)}
                        {r.prompt_summary && (
                          <div className="mt-2 p-2 bg-white border border-slate-200 rounded whitespace-pre-wrap break-words">
                            <span className="text-[10px] uppercase tracking-widest text-slate-400">prompt</span>
                            <div>{r.prompt_summary}</div>
                          </div>
                        )}
                        {r.error_detail && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-800 whitespace-pre-wrap break-all">
                            {r.error_detail}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}



function UserActivityTimeline({ userId }: { userId: string }) {
  const [data, setData] = useState<{
    api_calls: { id: string; method: string; path: string; status_code: number | null; duration_ms: number | null; created_at: string }[];
    page_views: { id: string; path: string; referrer: string | null; created_at: string }[];
    clicks: { id: string; path: string | null; track_key: string | null; element_label: string | null; created_at: string }[];
    group_edits: { id: string; group_type: string; group_id: string; field_path: string; old_value: { v: unknown }; new_value: { v: unknown }; created_at: string }[];
    creations: { id: string; entity_type: string; entity_id: string; action: string; summary: Record<string, unknown>; created_at: string }[];
  } | null>(null);
  useEffect(() => {
    api<typeof data>(`/users/${userId}/activity?range=30d&limit=300`).then(setData).catch(() => setData(null));
  }, [userId]);

  const events = useMemo(() => {
    if (!data) return [];
    type E = { ts: string; type: string; label: string; detail?: string; tone?: string };
    const out: E[] = [];
    data.page_views.forEach((r) => out.push({ ts: r.created_at, type: "page", label: `Viewed ${r.path}` }));
    data.clicks.forEach((r) => out.push({ ts: r.created_at, type: "click", label: `Clicked ${r.track_key || r.element_label || "?"}` }));
    data.group_edits.forEach((r) => out.push({ ts: r.created_at, type: "edit", label: `Edited ${r.group_type}/${r.group_id} · ${r.field_path}`,
      detail: `${JSON.stringify(r.old_value?.v).slice(0, 60)} → ${JSON.stringify(r.new_value?.v).slice(0, 60)}` }));
    data.creations.forEach((r) => out.push({ ts: r.created_at, type: "create", label: `${r.action} ${r.entity_type} (${r.entity_id})` }));
    data.api_calls.filter((c) => (c.status_code || 0) >= 400).forEach((r) =>
      out.push({ ts: r.created_at, type: "error", label: `${r.method} ${r.path} → ${r.status_code}`, tone: "danger" }));
    out.sort((a, b) => b.ts.localeCompare(a.ts));
    return out.slice(0, 200);
  }, [data]);

  if (!data) return <div className="text-xs text-slate-400 p-4">Loading…</div>;
  return (
    <div className="p-4 bg-slate-50 max-h-[400px] overflow-y-auto">
      <div className="space-y-1">
        {events.map((e, i) => (
          <div key={i} className={`text-xs flex gap-2 items-baseline ${e.tone === "danger" ? "text-red-700" : ""}`}>
            <span className="text-slate-400 font-mono text-[10px] w-20 shrink-0">{new Date(e.ts).toLocaleTimeString()}</span>
            <span className={`uppercase text-[9px] font-bold w-12 ${
              e.type === "edit" ? "text-emerald-600" :
              e.type === "create" ? "text-blue-600" :
              e.type === "click" ? "text-purple-600" :
              e.type === "error" ? "text-red-600" : "text-slate-400"
            }`}>{e.type}</span>
            <span className="text-slate-700">{e.label}</span>
            {e.detail && <span className="text-slate-400 font-mono">{e.detail}</span>}
          </div>
        ))}
        {events.length === 0 && <p className="text-xs text-slate-400">No activity in last 30 days.</p>}
      </div>
    </div>
  );
}

function GroupEditHistory({ groupType, groupId }: { groupType: string; groupId: string }) {
  const [rows, setRows] = useState<{ id: string; field_path: string; old_value: { v: unknown }; new_value: { v: unknown }; user_id: string | null; created_at: string }[]>([]);
  useEffect(() => {
    api<typeof rows>(`/groups/${groupType}/${groupId}/edits?range=90d`).then(setRows).catch(() => setRows([]));
  }, [groupType, groupId]);
  return (
    <div className="p-4 bg-slate-50 max-h-[400px] overflow-y-auto space-y-3">
      {rows.length === 0 && <p className="text-xs text-slate-400">No edits yet.</p>}
      {rows.map((r) => (
        <div key={r.id} className="text-xs border-l-2 border-emerald-300 pl-3">
          <div className="flex justify-between">
            <span className="font-bold text-slate-800">{r.field_path}</span>
            <span className="text-slate-400">{relTime(r.created_at)}</span>
          </div>
          <div className="font-mono text-[11px] mt-1 space-y-0.5">
            <div className="text-red-700 line-through opacity-70">{JSON.stringify(r.old_value?.v).slice(0, 200)}</div>
            <div className="text-emerald-700">{JSON.stringify(r.new_value?.v).slice(0, 200)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function AuditLogTable({ rows, range }: { rows: AuditLogRow[]; range: Range }) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [entityFilter, setEntityFilter] = useState("");
  const [q, setQ] = useState("");

  const entityTypes = useMemo(
    () => Array.from(new Set(rows.map((r) => r.entity_type))).sort(),
    [rows]
  );

  const filtered = rows.filter((r) => {
    if (entityFilter && r.entity_type !== entityFilter) return false;
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (
      r.label.toLowerCase().includes(needle) ||
      (r.user?.name || "").toLowerCase().includes(needle) ||
      (r.user?.email || "").toLowerCase().includes(needle)
    );
  });

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-xs font-black uppercase tracking-widest text-slate-700">Audit log — last {range}</p>
          <p className="text-[10px] text-slate-400 mt-1">
            Every staff-driven change to groups, assignments, events, volunteer profiles, policies, and admissions reviews. Click a row for full details.
          </p>
        </div>
        <div className="flex gap-2">
          <select value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)}
            className="text-xs border border-slate-200 rounded-md px-2 py-1.5 text-slate-600">
            <option value="">All entities</option>
            {entityTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search user or change…"
            className="text-xs border border-slate-200 rounded-md px-2 py-1.5 w-48 text-slate-600" />
        </div>
      </div>
      <div className="overflow-x-auto max-h-[600px]">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest sticky top-0">
            <tr>
              <th className="text-left px-4 py-2">Time</th>
              <th className="text-left px-4 py-2">Staff member</th>
              <th className="text-left px-4 py-2">Entity</th>
              <th className="text-left px-4 py-2">Change</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-400">No audited changes in this range.</td></tr>
            )}
            {filtered.map((r) => {
              const open = expanded === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr className="cursor-pointer hover:bg-slate-50" onClick={() => setExpanded(open ? null : r.id)}>
                    <td className="px-4 py-2 text-slate-500 font-mono whitespace-nowrap">{relTime(r.created_at)}</td>
                    <td className="px-4 py-2">
                      <span className={r.user ? "text-slate-700" : "text-slate-400 italic"}>{userLabel(r.user)}</span>
                    </td>
                    <td className="px-4 py-2">
                      <span className="capitalize font-bold text-slate-700">{r.entity_type.replace(/_/g, " ")}</span>
                      <span className="text-slate-400 font-mono ml-1 text-[10px]">{r.entity_id}</span>
                    </td>
                    <td className="px-4 py-2 text-slate-600">{r.label}</td>
                  </tr>
                  {open && (
                    <tr className="bg-slate-50">
                      <td colSpan={4} className="px-6 py-3 text-xs space-y-1 font-mono text-slate-600">
                        {r.user?.email && <div><b>staff email:</b> {r.user.email}</div>}
                        {r.kind === "field_edit" ? (
                          <>
                            <div><b>field:</b> {r.field_path}</div>
                            <div className="text-red-700 line-through opacity-70">{JSON.stringify(r.old_value).slice(0, 300)}</div>
                            <div className="text-emerald-700">{JSON.stringify(r.new_value).slice(0, 300)}</div>
                          </>
                        ) : (
                          <>
                            <div><b>action:</b> {r.action}</div>
                            {r.summary && (
                              <pre className="whitespace-pre-wrap break-all mt-1 p-2 bg-white border border-slate-200 rounded">
                                {JSON.stringify(r.summary, null, 2).slice(0, 1000)}
                              </pre>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface DashboardResponse {
  range: Range;
  summary: Summary;
  heatmap: Heatmap;
  dau: DauPoint[];
  events_series: EventsPoint[];
  status_breakdown: StatusRow[];
  role_distribution: RoleRow[];
  device_breakdown: DeviceBreakdown;
  coc_summary: CocSummary;
}

type Tab = "pulse" | "users" | "sessions" | "devices" | "auth" | "groups" | "audit" | "errors" | "llm" | "serp" | "emails" | "performance" | "all";

const TAB_LABELS: Record<Tab, string> = {
  pulse: "Pulse", users: "Users", sessions: "Sessions", devices: "Devices",
  auth: "Auth", groups: "Groups", audit: "Audit Log", errors: "Errors", llm: "LLM",
  serp: "SERP / Apify", emails: "Emails", performance: "Performance", all: "All API calls",
};

export function PlatformInsightsPanel() {
  const [range, setRange] = useState<Range>("7d");
  const [tab, setTab] = useState<Tab>("pulse");
  const [summary, setSummary] = useState<Summary | null>(null);
  const [recent, setRecent] = useState<ApiCallRow[]>([]);
  const [errors, setErrors] = useState<ApiCallRow[]>([]);
  const [llm, setLlm] = useState<LlmRow[]>([]);
  const [llmSummary, setLlmSummary] = useState<LlmSummaryRow[]>([]);
  const [serp, setSerp] = useState<SerpRow[]>([]);
  const [serpSummary, setSerpSummary] = useState<SerpSummaryRow[]>([]);
  const [emailSummary, setEmailSummary] = useState<EmailSummary | null>(null);
  const [emailLog, setEmailLog] = useState<EmailLogRow[]>([]);
  const [draftEdits, setDraftEdits] = useState<DraftEditRow[]>([]);
  const [uploadSummary, setUploadSummary] = useState<UploadSummaryRow[]>([]);
  const [authSummary, setAuthSummary] = useState<AuthSummaryRow[]>([]);
  const [authEvents, setAuthEvents] = useState<AuthEventRow[]>([]);
  const [cocSummary, setCocSummary] = useState<CocSummary | null>(null);
  const [dau, setDau] = useState<DauPoint[]>([]);
  const [eventsSeries, setEventsSeries] = useState<EventsPoint[]>([]);
  const [deviceBreakdown, setDeviceBreakdown] = useState<DeviceBreakdown | null>(null);
  const [sessionSummary, setSessionSummary] = useState<SessionSummary | null>(null);
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [jsErrors, setJsErrors] = useState<InteractionRow[]>([]);
  const [heatmap, setHeatmap] = useState<Heatmap | null>(null);
  const [statusBreakdown, setStatusBreakdown] = useState<StatusRow[]>([]);
  const [roleDist, setRoleDist] = useState<RoleRow[]>([]);
  const [activeUsers, setActiveUsers] = useState<ActiveUserRow[]>([]);
  const [groupActivity, setGroupActivity] = useState<GroupActivityRow[]>([]);
  const [auditLog, setAuditLog] = useState<AuditLogRow[]>([]);
  const [slowRoutes, setSlowRoutes] = useState<SlowRoute[]>([]);
  const [userFilter, setUserFilter] = useState("");
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [drill, setDrill] = useState<DrillFilter | null>(null);
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncingBrevo, setSyncingBrevo] = useState(false);
  const [syncMsg, setSyncMsg] = useState<{ text: string; error?: boolean } | null>(null);
  const loadedTabsRef = useRef<Set<string>>(new Set());
  const { replay: replayTour } = useAutoTour("staff.insights", TOURS["staff.insights"]);

  const handleSyncBrevo = async () => {
    setSyncingBrevo(true);
    setSyncMsg(null);
    try {
      const res = await api<{ success: boolean; message: string }>(`/sync-brevo-events?days=14`, {
        method: "POST",
      });
      setSyncMsg({ text: res.message });
      const [esum, elog] = await Promise.all([
        api<EmailSummary>(`/email-summary?range=${range}`),
        api<EmailLogRow[]>(`/email-log?range=${range}&limit=1500`),
      ]);
      setEmailSummary(esum);
      setEmailLog(elog);
    } catch (err: any) {
      setSyncMsg({ text: err.message || "Failed to sync Brevo tracking", error: true });
    } finally {
      setSyncingBrevo(false);
    }
  };

  // 1. Unified Dashboard Initial Mount & Range Change (1 single API call)
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api<DashboardResponse>(`/dashboard?range=${range}`)
      .then((d) => {
        if (cancelled) return;
        setSummary(d.summary);
        setHeatmap(d.heatmap);
        setDau(d.dau);
        setEventsSeries(d.events_series);
        setStatusBreakdown(d.status_breakdown);
        setRoleDist(d.role_distribution);
        setDeviceBreakdown(d.device_breakdown);
        setCocSummary(d.coc_summary);
      })
      .catch((e) => !cancelled && setError(String(e)))
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [range]);

  // 2. Lazy On-Demand Tab Loading (fetches only when a specific sub-tab is opened)
  useEffect(() => {
    if (tab === "pulse" || tab === "devices") return;

    const cacheKey = `${range}:${tab}`;
    if (loadedTabsRef.current.has(cacheKey)) return;

    let cancelled = false;
    setTabLoading(true);

    const fetchTabData = async () => {
      try {
        switch (tab) {
          case "users": {
            const au = await api<ActiveUserRow[]>(`/active-users?range=${range}&limit=200`);
            if (!cancelled) setActiveUsers(au);
            break;
          }
          case "sessions": {
            const [ssum, sess] = await Promise.all([
              api<SessionSummary>(`/session-summary?range=${range}`),
              api<SessionRow[]>(`/sessions?range=${range}&limit=200`),
            ]);
            if (!cancelled) {
              setSessionSummary(ssum);
              setSessions(sess);
            }
            break;
          }
          case "auth": {
            const [asum, aev] = await Promise.all([
              api<AuthSummaryRow[]>(`/auth-summary?range=${range}`),
              api<AuthEventRow[]>(`/auth-events?range=${range}&limit=200`),
            ]);
            if (!cancelled) {
              setAuthSummary(asum);
              setAuthEvents(aev);
            }
            break;
          }
          case "groups": {
            const ga = await api<GroupActivityRow[]>(`/group-activity?range=${range}&limit=100`);
            if (!cancelled) setGroupActivity(ga);
            break;
          }
          case "audit": {
            const al = await api<AuditLogRow[]>(`/audit-log?range=${range}&limit=300`);
            if (!cancelled) setAuditLog(al);
            break;
          }
          case "errors": {
            const [e, jse] = await Promise.all([
              api<ApiCallRow[]>(`/errors?range=${range}&limit=100`),
              api<InteractionRow[]>(`/interactions?kind=js_error&range=${range}&limit=200`),
            ]);
            if (!cancelled) {
              setErrors(e);
              setJsErrors(jse);
            }
            break;
          }
          case "llm": {
            const [ls, l] = await Promise.all([
              api<LlmSummaryRow[]>(`/llm-summary?range=${range}`),
              api<LlmRow[]>(`/llm-calls?range=${range}&limit=100`),
            ]);
            if (!cancelled) {
              setLlmSummary(ls);
              setLlm(l);
            }
            break;
          }
          case "serp": {
            const [ss, sp] = await Promise.all([
              api<SerpSummaryRow[]>(`/serp-summary?range=${range}`),
              api<SerpRow[]>(`/serp-calls?range=${range}&limit=100`),
            ]);
            if (!cancelled) {
              setSerpSummary(ss);
              setSerp(sp);
            }
            break;
          }
          case "emails": {
            const [esum, elog, dedit] = await Promise.all([
              api<EmailSummary>(`/email-summary?range=${range}`),
              api<EmailLogRow[]>(`/email-log?range=${range}&limit=2000`),
              api<DraftEditRow[]>(`/draft-edits?range=${range}&limit=200`),
            ]);
            if (!cancelled) {
              setEmailSummary(esum);
              setEmailLog(elog);
              setDraftEdits(dedit);
            }
            break;
          }
          case "performance": {
            const sr = await api<SlowRoute[]>(`/slow-routes?range=${range}`);
            if (!cancelled) setSlowRoutes(sr);
            break;
          }
          case "all": {
            const r = await api<ApiCallRow[]>(`/recent-calls?limit=100`);
            if (!cancelled) setRecent(r);
            break;
          }
        }
        if (!cancelled) {
          loadedTabsRef.current.add(cacheKey);
        }
      } catch (err) {
        if (!cancelled) setError(String(err));
      } finally {
        if (!cancelled) setTabLoading(false);
      }
    };

    fetchTabData();

    return () => {
      cancelled = true;
    };
  }, [range, tab]);

  const filteredUsers = activeUsers.filter((u) => {
    if (!userFilter.trim()) return true;
    const q = userFilter.toLowerCase();
    return (u.user?.name || "").toLowerCase().includes(q) ||
           (u.user?.email || "").toLowerCase().includes(q);
  });

  const errCount = (summary?.totals.errors_4xx || 0) + (summary?.totals.errors_5xx || 0);
  const errPct = summary && summary.totals.api_calls > 0
    ? ((errCount / summary.totals.api_calls) * 100).toFixed(1) : "0";

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Platform Insights</h2>
          <p className="text-sm text-slate-500 mt-1">
            Granular activity tracking across every API call, page view, click, edit, LLM call, and session.
          </p>
        </div>
        <div className="flex items-center gap-4">
        <TourButton onClick={replayTour} />
        <div id="insights-range" className="flex gap-1 bg-slate-100 rounded-lg p-1">
          {(["1d", "7d", "30d", "90d"] as Range[]).map((r) => (
            <button key={r} data-track={`insights-range-${r}`} onClick={() => setRange(r)}
              className={`px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${
                range === r ? "bg-white text-[#003d45] shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}>{r}</button>
          ))}
        </div>
        </div>
      </div>

      {error && <p className="text-sm text-red-600">Error: {error}</p>}
      {loading && <p className="text-sm text-slate-500">Loading…</p>}
      {!loading && tabLoading && <p className="text-sm text-slate-500 animate-pulse">Loading {TAB_LABELS[tab]} data…</p>}

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card label="Active users" value={summary.totals.active_users} hint={`unique people in last ${range}`} />
          <Card label="API calls" value={summary.totals.api_calls} hint={`${errPct}% error rate`} />
          <Card label="Page views" value={summary.totals.page_views} />
          <Card label="Tracked clicks" value={summary.totals.clicks} />
          <Card label="Group edits" value={summary.totals.group_edits} />
          <Card label="Entities created" value={summary.totals.entities_created} hint="meetings, events" />
          <Card label="Failed requests (user side · 4xx)" value={summary.totals.errors_4xx} tone={summary.totals.errors_4xx > 0 ? "warn" : undefined} />
          <Card label="Server crashes (our bugs · 5xx)" value={summary.totals.errors_5xx} tone={summary.totals.errors_5xx > 0 ? "danger" : undefined} />
          <Card label="AI / LLM calls" value={summary.totals.llm_calls} />
          <Card label="AI / LLM failures" value={summary.totals.llm_failed} tone={summary.totals.llm_failed > 0 ? "danger" : undefined} />
          <Card label="AI tokens used (est.)" value={summary.totals.llm_total_tokens.toLocaleString()} hint="input + output tokens" tooltip="Tokens are logged from provider API responses. The count is accurate when the provider returns usage data; calls that omit usage fields are not counted." />
          <Card label="AI spend (est.)" value={`$${summary.totals.llm_cost_usd.toFixed(4)}`} hint="estimate · can change over time" tooltip={"Formula: input_tokens × input_rate + output_tokens × output_rate, applied per provider and model.\n\nSample rates (USD/token): Azure gpt-4.1-mini $0.00000040 in / $0.0000016 out · Gemini $0.00000075 in / $0.000003 out · Anthropic Sonnet $0.000003 in / $0.000015 out.\n\nThis figure can change over time: it is recomputed from the rate table on every load, so updating the rates or back-filling missing usage data will move past totals.\n\nRates are maintained in one place — backend/app/api/routes/insights.py — and may not reflect the latest provider pricing. Actual charges from your provider may differ."} />
          <Card label="SERP API calls" value={summary.totals.serp_calls} />
          <Card label="SERP failures" value={summary.totals.serp_failed} tone={summary.totals.serp_failed > 0 ? "danger" : undefined} />
          <Card label="Apify LinkedIn calls" value={summary.totals.apify_calls} hint="deep profile scrapes" tooltip="Apify HarvestAPI LinkedIn profile fetches used for bio enrichment. Logged with an 'apify' purpose prefix and counted separately from SERP (Google) calls." />
          <Card label="Apify failures" value={summary.totals.apify_failed} tone={summary.totals.apify_failed > 0 ? "danger" : undefined} />
          <Card label="Apify est. spend" value={`$${(summary.totals.apify_cost_usd ?? 0).toFixed(2)}`} hint="$4 / 1k profiles" tooltip="Estimated Apify HarvestAPI spend for the selected range, based on successful profile scrapes billed at $0.004 each (pay-per-result)." />
          <Card label="File uploads" value={summary.totals.uploads} hint="photos, headshots, event media" />
          <Card label="Logins" value={summary.totals.logins} hint={`in last ${range}`} />
          <Card label="Failed logins" value={summary.totals.login_failures} tone={summary.totals.login_failures > 0 ? "warn" : undefined} hint="bad credentials" />
        </div>
      )}

      <div id="insights-tabs" className="flex gap-1 border-b border-slate-200 flex-wrap">
        {(["pulse", "users", "sessions", "devices", "auth", "groups", "audit", "errors", "llm", "serp", "emails", "performance", "all"] as Tab[]).map((t) => (
          <button key={t} data-track={`insights-tab-${t}`} onClick={() => setTab(t)}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors ${
              tab === t ? "text-[#003d45] border-b-2 border-[#003d45] -mb-px" : "text-slate-500 hover:text-slate-700"
            }`}>{TAB_LABELS[t]}</button>
        ))}
      </div>

      {tab === "pulse" && summary && (
        <div className="space-y-4">
          <ActivityHeatmap data={heatmap} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <EventsTrendChart data={eventsSeries} onDrillDay={(day) => setDrill({ title: `API calls on ${day}`, query: `day=${day}` })} />
            <DauChart data={dau} onDrillDay={(day) => setDrill({ title: `API calls on ${day}`, query: `day=${day}` })} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatusBreakdown data={statusBreakdown} onDrillStatus={(cls) => setDrill({ title: `${cls} responses`, query: `status_class=${cls}` })} />
            <ChartCard title="Active users by role">
              <Donut rows={roleDist.map((r) => ({ label: r.role, count: r.users }))} />
            </ChartCard>
            <ChartCard title="Sessions by device">
              <Donut rows={deviceBreakdown?.devices || []} />
            </ChartCard>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ChartCard title="Top pages" hint="page views">
              <BarChart rows={summary.top_pages.map((p) => ({ label: p.path, count: p.count }))} />
            </ChartCard>
            <ChartCard title="Top tracked clicks" hint="by track key">
              <BarChart rows={summary.top_clicks.map((c) => ({ label: c.track_key, count: c.count }))} color={TEAL} />
            </ChartCard>
            <ChartCard title="Top API routes" hint="click a bar to inspect">
              <BarChart rows={summary.top_routes.map((r) => ({ label: r.path, count: r.count }))}
                onDrillLabel={(path) => setDrill({ title: `Route ${path}`, query: `path=${encodeURIComponent(path)}` })} />
            </ChartCard>
            <ChartCard title="Top editors" hint="group field edits">
              <BarChart rows={summary.top_editors.map((e) => ({ label: userLabel(e.user), count: e.count }))} color={TEAL} />
            </ChartCard>
          </div>
        </div>
      )}

      {tab === "users" && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
            <p className="text-xs font-black uppercase tracking-widest text-slate-700">Active users — last {range}</p>
            <input value={userFilter} onChange={(e) => setUserFilter(e.target.value)}
              placeholder="Filter by name or email…"
              className="text-xs border border-slate-300 rounded px-3 py-1.5 w-64" />
          </div>
          <div className="overflow-x-auto max-h-[600px]">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2">User</th>
                  <th className="text-left px-4 py-2">Email</th>
                  <th className="text-left px-4 py-2">Role</th>
                  <th className="text-right px-4 py-2">API calls</th>
                  <th className="text-right px-4 py-2">Page views</th>
                  <th className="text-right px-4 py-2">Clicks</th>
                  <th className="text-right px-4 py-2">Edits</th>
                  <th className="text-left px-4 py-2">Last seen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredUsers.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-400">No users matched.</td></tr>
                )}
                {filteredUsers.map((u) => {
                  const uid = u.user?.id || "anon";
                  const open = expandedUser === uid;
                  return (
                    <React.Fragment key={uid}>
                      <tr className="cursor-pointer hover:bg-slate-50" onClick={() => setExpandedUser(open ? null : uid)}>
                        <td className="px-4 py-2 font-bold text-slate-800">{userLabel(u.user)}</td>
                        <td className="px-4 py-2 text-slate-500">{u.user?.email || "—"}</td>
                        <td className="px-4 py-2 text-slate-500 capitalize">{u.user?.role || "—"}</td>
                        <td className="px-4 py-2 text-right font-mono">{u.api_calls}</td>
                        <td className="px-4 py-2 text-right font-mono">{u.page_views}</td>
                        <td className="px-4 py-2 text-right font-mono">{u.clicks}</td>
                        <td className="px-4 py-2 text-right font-mono">{u.edits}</td>
                        <td className="px-4 py-2 text-slate-500">{relTime(u.last_seen)}</td>
                      </tr>
                      {open && u.user && (
                        <tr><td colSpan={8} className="p-0"><UserActivityTimeline userId={u.user.id} /></td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "sessions" && (
        <div className="space-y-4">
          {sessionSummary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card label="Sessions" value={sessionSummary.sessions} hint={`last ${range}`} />
              <Card label="Avg pages / session" value={sessionSummary.avg_pages_per_session} />
              <Card label="Avg duration" value={fmtDuration(sessionSummary.avg_duration_secs)} />
              <Card label="Bounce rate" value={`${sessionSummary.bounce_rate_pct}%`}
                tone={sessionSummary.bounce_rate_pct > 60 ? "warn" : undefined} hint="single-page visits" />
            </div>
          )}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200">
              <p className="text-xs font-black uppercase tracking-widest text-slate-700">Recent sessions — last {range}</p>
              <p className="text-[10px] text-slate-400 mt-1">A session = one browser tab visit, grouped by session id.</p>
            </div>
            <div className="overflow-x-auto max-h-[600px]">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2">Started</th>
                    <th className="text-left px-4 py-2">User</th>
                    <th className="text-right px-4 py-2">Pages</th>
                    <th className="text-right px-4 py-2">Duration</th>
                    <th className="text-left px-4 py-2">Entry</th>
                    <th className="text-left px-4 py-2">Exit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sessions.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-400">No sessions yet.</td></tr>
                  )}
                  {sessions.map((s) => (
                    <tr key={s.session_id} className="hover:bg-slate-50">
                      <td className="px-4 py-2 text-slate-500 whitespace-nowrap">{s.started ? relTime(s.started) : "—"}</td>
                      <td className="px-4 py-2 text-slate-700">{userLabel(s.user)}</td>
                      <td className="px-4 py-2 text-right font-mono">{s.pages}</td>
                      <td className="px-4 py-2 text-right font-mono text-slate-500">{fmtDuration(s.duration_secs)}</td>
                      <td className="px-4 py-2 font-mono text-slate-500 truncate max-w-[180px]" title={s.entry_page ?? ""}>{s.entry_page ?? "—"}</td>
                      <td className="px-4 py-2 font-mono text-slate-500 truncate max-w-[180px]" title={s.exit_page ?? ""}>{s.exit_page ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === "devices" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ChartCard title="By browser">
            <Donut rows={deviceBreakdown?.browsers || []} />
          </ChartCard>
          <ChartCard title="By operating system">
            <Donut rows={deviceBreakdown?.os || []} />
          </ChartCard>
          <ChartCard title="By device type">
            <Donut rows={deviceBreakdown?.devices || []} />
          </ChartCard>
          <ChartCard title="Browsers" hint="requests" className="md:col-span-3">
            <BarChart rows={deviceBreakdown?.browsers || []} />
          </ChartCard>
        </div>
      )}

      {tab === "auth" && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <p className="text-xs font-black uppercase tracking-widest text-slate-700 mb-3">Auth events by type — last {range}</p>
              <table className="w-full text-xs">
                <thead className="text-slate-500 uppercase text-[10px] tracking-widest">
                  <tr>
                    <th className="text-left py-1">Event</th>
                    <th className="text-right py-1">Count</th>
                    <th className="text-right py-1">Unique users</th>
                  </tr>
                </thead>
                <tbody className="font-mono divide-y divide-slate-100">
                  {authSummary.length === 0 && (
                    <tr><td colSpan={3} className="py-3 text-center text-slate-400">No auth activity yet.</td></tr>
                  )}
                  {authSummary.map((r, i) => (
                    <tr key={i}>
                      <td className={`py-1.5 font-bold ${r.event === "login_failed" ? "text-amber-600" : "text-slate-700"}`}>{r.event}</td>
                      <td className="py-1.5 text-right">{r.count}</td>
                      <td className="py-1.5 text-right text-slate-500">{r.users}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <RankedList title="Auth events" rows={authSummary.map((a) => ({ label: a.event, count: a.count }))} />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200">
              <p className="text-xs font-black uppercase tracking-widest text-slate-700">Recent auth events — last {range}</p>
              <p className="text-[10px] text-slate-400 mt-1">Logins, failed logins, signups, password changes & recoveries.</p>
            </div>
            <div className="overflow-x-auto max-h-[560px]">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2">Time</th>
                    <th className="text-left px-4 py-2">Event</th>
                    <th className="text-left px-4 py-2">User</th>
                    <th className="text-left px-4 py-2">IP</th>
                    <th className="text-left px-4 py-2">Device</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {authEvents.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">No auth events yet.</td></tr>
                  )}
                  {authEvents.map((r) => {
                    const failed = r.event === "login_failed";
                    return (
                      <tr key={r.id} className={failed ? "bg-amber-50/40" : ""}>
                        <td className="px-4 py-2 text-slate-500 font-mono whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</td>
                        <td className={`px-4 py-2 font-bold font-mono ${failed ? "text-amber-600" : "text-slate-700"}`}>{r.event}</td>
                        <td className="px-4 py-2">
                          <span className={r.user ? "text-slate-700" : "text-slate-400 italic"}>{userLabel(r.user)}</span>
                          {r.user?.email && <span className="text-slate-400"> · {r.user.email}</span>}
                        </td>
                        <td className="px-4 py-2 text-slate-400 font-mono">{r.ip ?? "—"}</td>
                        <td className="px-4 py-2 text-slate-400 truncate max-w-xs" title={r.user_agent ?? ""}>{r.user_agent ?? "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* CoC acceptances */}
          <div className="grid grid-cols-2 gap-4">
            <Card label="CoC acceptances (all time)" value={cocSummary?.total ?? "—"} />
            <Card label="Unique volunteers accepted" value={cocSummary?.unique_users ?? "—"} />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200">
              <p className="text-xs font-black uppercase tracking-widest text-slate-700">Code of Conduct acceptance log</p>
              <p className="text-[10px] text-slate-400 mt-1">All-time record of every volunteer who accepted the CoC, with version and IP.</p>
            </div>
            <div className="overflow-x-auto max-h-[400px]">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2">Time</th>
                    <th className="text-left px-4 py-2">Volunteer</th>
                    <th className="text-left px-4 py-2">Version</th>
                    <th className="text-left px-4 py-2">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(!cocSummary || cocSummary.rows.length === 0) && (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-400">No acceptances recorded yet.</td></tr>
                  )}
                  {cocSummary?.rows.map((r) => (
                    <tr key={r.id}>
                      <td className="px-4 py-2 text-slate-500 font-mono whitespace-nowrap">{r.accepted_at ? new Date(r.accepted_at).toLocaleString() : "—"}</td>
                      <td className="px-4 py-2">
                        <span className={r.user ? "text-slate-700" : "text-slate-400 italic"}>{userLabel(r.user)}</span>
                        {r.user?.email && <span className="text-slate-400"> · {r.user.email}</span>}
                      </td>
                      <td className="px-4 py-2 font-mono text-teal-700 font-bold">{r.version}</td>
                      <td className="px-4 py-2 text-slate-400 font-mono">{r.ip ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === "groups" && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200">
            <p className="text-xs font-black uppercase tracking-widest text-slate-700">Group activity — last {range}</p>
            <p className="text-[10px] text-slate-400 mt-1">Click a group to see field-level edit history.</p>
          </div>
          <div className="overflow-x-auto max-h-[600px]">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2">Type</th>
                  <th className="text-left px-4 py-2">Group ID</th>
                  <th className="text-right px-4 py-2">Edits</th>
                  <th className="text-right px-4 py-2">Editors</th>
                  <th className="text-left px-4 py-2">Last edited</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {groupActivity.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">No group edits yet.</td></tr>
                )}
                {groupActivity.map((g) => {
                  const key = `${g.group_type}/${g.group_id}`;
                  const open = expandedGroup === key;
                  return (
                    <React.Fragment key={key}>
                      <tr className="cursor-pointer hover:bg-slate-50" onClick={() => setExpandedGroup(open ? null : key)}>
                        <td className="px-4 py-2 capitalize font-bold">{g.group_type}</td>
                        <td className="px-4 py-2 font-mono text-slate-500">{g.group_id}</td>
                        <td className="px-4 py-2 text-right font-mono">{g.edits}</td>
                        <td className="px-4 py-2 text-right font-mono">{g.editors}</td>
                        <td className="px-4 py-2 text-slate-500">{relTime(g.last_edited)}</td>
                      </tr>
                      {open && (
                        <tr><td colSpan={5} className="p-0"><GroupEditHistory groupType={g.group_type} groupId={g.group_id} /></td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "audit" && <AuditLogTable rows={auditLog} range={range} />}

      {tab === "errors" && (
        <div className="space-y-4">
          <ApiCallTable rows={errors}
            title={`Backend failures — last ${range}  ·  4xx = user-side (auth, bad input, not found)  ·  5xx = server crashes (our bugs)`} />
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200">
              <p className="text-xs font-black uppercase tracking-widest text-slate-700">Frontend JS errors — last {range}</p>
              <p className="text-[10px] text-slate-400 mt-1">Uncaught exceptions & unhandled promise rejections captured in the browser.</p>
            </div>
            <div className="overflow-x-auto max-h-[480px]">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2">Time</th>
                    <th className="text-left px-4 py-2">Message</th>
                    <th className="text-left px-4 py-2">Page</th>
                    <th className="text-left px-4 py-2">User</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {jsErrors.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-400">No JS errors 🎉</td></tr>
                  )}
                  {jsErrors.map((r) => {
                    const p = r.payload || {};
                    return (
                      <tr key={r.id} className="bg-red-50/30 align-top">
                        <td className="px-4 py-2 text-slate-500 font-mono whitespace-nowrap">{relTime(r.created_at)}</td>
                        <td className="px-4 py-2 text-red-700 font-mono break-all max-w-md">
                          {String(p.message ?? "—")}
                          {p.source ? <span className="block text-[10px] text-slate-400">{String(p.source)}{p.line ? `:${p.line}` : ""}</span> : null}
                        </td>
                        <td className="px-4 py-2 font-mono text-slate-500">{r.path ?? "—"}</td>
                        <td className="px-4 py-2 text-slate-600">{userLabel(r.user)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === "llm" && (
        <>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-xs font-black uppercase tracking-widest text-slate-700 mb-3">LLM calls by purpose</p>
            <table className="w-full text-xs">
              <thead className="text-slate-500 uppercase text-[10px] tracking-widest">
                <tr>
                  <th className="text-left py-1">Provider</th><th className="text-left py-1">Purpose</th>
                  <th className="text-right py-1">Calls</th><th className="text-right py-1">Failures</th>
                  <th className="text-right py-1">Avg ms</th><th className="text-right py-1">In tokens</th>
                  <th className="text-right py-1">Out tokens</th><th className="text-right py-1">Est. cost</th>
                </tr>
              </thead>
              <tbody className="font-mono divide-y divide-slate-100">
                {llmSummary.length === 0 && (
                  <tr><td colSpan={8} className="py-3 text-center text-slate-400">No LLM activity yet.</td></tr>
                )}
                {llmSummary.map((r, i) => (
                  <tr key={i}>
                    <td className="py-1.5">{r.provider}</td>
                    <td className="py-1.5 text-slate-600">{r.purpose ?? "—"}</td>
                    <td className="py-1.5 text-right">{r.count}</td>
                    <td className={`py-1.5 text-right ${r.failures > 0 ? "text-red-600 font-bold" : ""}`}>{r.failures}</td>
                    <td className="py-1.5 text-right">{Math.round(r.avg_ms)}</td>
                    <td className="py-1.5 text-right">{r.input_tokens.toLocaleString()}</td>
                    <td className="py-1.5 text-right">{r.output_tokens.toLocaleString()}</td>
                    <td className="py-1.5 text-right font-semibold">${r.cost_usd.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <LlmCallTable rows={llm} title={`Recent LLM calls — last ${range}`} />
        </>
      )}

      {tab === "serp" && (
        <>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-xs font-black uppercase tracking-widest text-slate-700 mb-3">SERP &amp; Apify calls by purpose</p>
            <p className="text-[11px] text-slate-400 mb-3">Apify LinkedIn scrapes (purpose <span className="font-mono">apify_*</span>) are billed $0.004 each and show an estimated spend; SERP (Google) calls have no per-call cost here.</p>
            <table className="w-full text-xs">
              <thead className="text-slate-500 uppercase text-[10px] tracking-widest">
                <tr>
                  <th className="text-left py-1">Purpose</th>
                  <th className="text-right py-1">Calls</th>
                  <th className="text-right py-1">Failures</th>
                  <th className="text-right py-1">Avg ms</th>
                  <th className="text-right py-1">Total results</th>
                  <th className="text-right py-1">Est. cost</th>
                </tr>
              </thead>
              <tbody className="font-mono divide-y divide-slate-100">
                {serpSummary.length === 0 && (
                  <tr><td colSpan={6} className="py-3 text-center text-slate-400">No SERP activity yet.</td></tr>
                )}
                {serpSummary.map((r, i) => (
                  <tr key={i}>
                    <td className="py-1.5 text-slate-600">{r.purpose ?? "—"}</td>
                    <td className="py-1.5 text-right">{r.count}</td>
                    <td className={`py-1.5 text-right ${r.failures > 0 ? "text-red-600 font-bold" : ""}`}>{r.failures}</td>
                    <td className="py-1.5 text-right">{Math.round(r.avg_ms)}</td>
                    <td className="py-1.5 text-right">{r.total_results}</td>
                    <td className="py-1.5 text-right">{r.cost_usd > 0 ? `$${r.cost_usd.toFixed(2)}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200">
              <p className="text-xs font-black uppercase tracking-widest text-slate-700">Recent SERP &amp; Apify calls — last {range}</p>
            </div>
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest">
                <tr>
                  <th className="text-left px-4 py-2">Time</th>
                  <th className="text-left px-4 py-2">Purpose</th>
                  <th className="text-left px-4 py-2">Query</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-right px-4 py-2">Duration</th>
                  <th className="text-right px-4 py-2">Results</th>
                  <th className="text-left px-4 py-2">User</th>
                </tr>
              </thead>
              <tbody className="font-mono divide-y divide-slate-100">
                {serp.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-400">No SERP calls yet.</td></tr>
                )}
                {serp.map((r) => (
                  <tr key={r.id} className={r.success ? "" : "bg-red-50"}>
                    <td className="px-4 py-2 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="px-4 py-2">{r.purpose ?? "—"}</td>
                    <td className="px-4 py-2 max-w-xs truncate text-slate-500">{r.query_summary ?? "—"}</td>
                    <td className="px-4 py-2">
                      {r.success
                        ? <span className="text-emerald-600 font-bold">ok</span>
                        : <span className="text-red-600 font-bold">{r.error_type ?? "fail"}</span>}
                    </td>
                    <td className="px-4 py-2 text-right">{r.duration_ms != null ? `${r.duration_ms}ms` : "—"}</td>
                    <td className="px-4 py-2 text-right">{r.result_count ?? "—"}</td>
                    <td className="px-4 py-2">{r.user ? (r.user.name || r.user.email || r.user.id) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "emails" && (
        <StaffEmailInsights
          summary={emailSummary}
          initialLogs={emailLog}
          draftEdits={draftEdits}
          range={range}
          onRangeChange={(newRange) => {
            setRange(newRange as Range);
            loadedTabsRef.current.delete("emails");
          }}
          onRefresh={async () => {
            try {
              const [esum, elog, dedit] = await Promise.all([
                api<EmailSummary>(`/email-summary?range=${range}`),
                api<EmailLogRow[]>(`/email-log?range=${range}&limit=2000`),
                api<DraftEditRow[]>(`/draft-edits?range=${range}&limit=200`),
              ]);
              setEmailSummary(esum);
              setEmailLog(elog);
              setDraftEdits(dedit);
            } catch (e) {
              console.error("Failed to refresh email insights", e);
            }
          }}
        />
      )}

      {tab === "performance" && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200">
            <p className="text-xs font-black uppercase tracking-widest text-slate-700">Slowest routes (p95 latency)</p>
            <p className="text-[10px] text-slate-400 mt-1">Anything consistently over 500ms is a candidate for optimisation.</p>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest">
              <tr>
                <th className="text-left px-4 py-2">Method</th><th className="text-left px-4 py-2">Path</th>
                <th className="text-right px-4 py-2">Calls</th><th className="text-right px-4 py-2">Avg</th>
                <th className="text-right px-4 py-2">p95</th><th className="text-right px-4 py-2">Max</th>
              </tr>
            </thead>
            <tbody className="font-mono divide-y divide-slate-100">
              {slowRoutes.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-400">No data yet.</td></tr>
              )}
              {slowRoutes.map((r, i) => (
                <tr key={i} className={r.p95_ms > 500 ? "bg-amber-50/30" : ""}>
                  <td className="px-4 py-1.5 font-bold">{r.method}</td>
                  <td className="px-4 py-1.5">{r.path}</td>
                  <td className="px-4 py-1.5 text-right">{r.count}</td>
                  <td className="px-4 py-1.5 text-right">{Math.round(r.avg_ms)}ms</td>
                  <td className={`px-4 py-1.5 text-right font-bold ${r.p95_ms > 500 ? "text-amber-600" : ""}`}>{Math.round(r.p95_ms)}ms</td>
                  <td className="px-4 py-1.5 text-right text-slate-500">{r.max_ms}ms</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "all" && <ApiCallTable rows={recent} title="Recent API calls (latest 100)" />}

      {drill && <DrillDownDrawer filter={drill} range={range} onClose={() => setDrill(null)} />}
    </div>
  );
}
